// src/context/hooks/useAdviserDetails.js
//  General-purpose hook: can be reused across apps (Recruitment, Financial Promotions, Check², etc.)
// - Fetches adviser data from Firestore using your existing "Authorized Members/{role}/Data" structure
// - Supports Admin users by allowing a "targetEmail" override (e.g., chosen adviser from a dropdown)
// - Returns a normalized "baseData" object (matching the shape you posted)
// - Derives entityType: Ltd_AR | Sole_Trader_AR | Trading_Style | unknown
//
// Usage:
//   const { adviser, entityType, loading, error, refetch } = useAdviserDetails();
//
//   // or for Admin viewing another adviser:
//   const { adviser, entityType } = useAdviserDetails({ targetEmail: selectedEmail });
//
// Notes:
// - If you later add a definitive field (e.g. baseData.arEntityType), wire it into deriveEntityType().

import { useCallback, useEffect, useMemo, useState } from "react";
import { firestore } from "../../backend/firebaseconfig";
import { collection, getDocs, query, where, limit } from "firebase/firestore";

import { useUserEmail } from "../UserEmailContext";
import { useUserRole } from "../UserRoleContext";
import { useAdminAdviserMails } from "../AdminAdviserEmailsContext";

// If you already have formatUKPhone in a shared util, import it and replace the stubs.
const formatUKPhone = (v) => (v ? String(v).trim() : "");

/**
 * @typedef {"Ltd_AR"|"Sole_Trader_AR"|"Trading_Style"|"unknown"} EntityType
 */

/**
 * Derive entity type from your baseData.
 * Update this function if you add a proper discriminator field in Firestore.
 * @param {object|null} adviser
 * @returns {EntityType}
 */
function deriveEntityType(adviser) {
  if (!adviser) return "unknown";

  const hasTradingStyle =
    !!(adviser.tradingStyle && String(adviser.tradingStyle).trim()) ||
    (Array.isArray(adviser.tradingStyles) && adviser.tradingStyles.length > 0);

  if (hasTradingStyle) return "Trading_Style";

  if (adviser.isAR) {
    // If you later add a field like adviser.arType = "sole" | "ltd"
    // you can return "Sole_Trader_AR" accordingly.
    return "Ltd_AR";
  }

  return "unknown";
}

/**
 * Normalize Firestore document data into a consistent object shape.
 * Matches the structure you posted.
 */
function normalizeAdviser(docSnap) {
  const raw = docSnap?.data?.() ? docSnap.data() : docSnap || {};
  const baseData = raw || {};
  const personal = baseData?.personalDetails || baseData?.personal || {};

  return {
    id: docSnap?.id || baseData?.id || "",
    firstName: personal?.forename || baseData?.firstName || "",
    preferredName: personal?.preferredName || baseData?.preferredName || "",
    lastName: personal?.surname || baseData?.lastName || "",
    email: baseData?.email || docSnap?.id || "-",
    status: baseData?.status || [],
    tradingStyles: baseData?.tradingStyles || [],
    mobileNumber:
      formatUKPhone(personal?.mobileTelephone) ||
      formatUKPhone(personal?.homeTelephone) ||
      "",
    officeNumber: formatUKPhone(personal?.officeTelephone) || "",
    fcaNumber: personal?.fcaNumber || baseData?.fcaNumber || "",
    businessAddress: personal?.businessAddress?.fullAddress || "",
    tradingStyle: baseData?.tradingStyle || "",
    tradingName: baseData?.tradingName || "",
    isAR: !!baseData?.isAR,

    // Keep a reference to raw data for edge cases (optional)
    _raw: baseData,
  };
}

/**
 * General purpose hook for adviser details
 *
 * @param {object} [opts]
 * @param {string|null} [opts.targetEmail] - override email (Admin selecting an adviser)
 * @param {string|null} [opts.roleOverride] - override role path (rare)
 * @param {string} [opts.collectionRoot] - defaults to "Authorized Members"
 */
export function useAdviserDetails(opts = {}) {
  const {
    targetEmail = null,
    roleOverride = null,
    collectionRoot = "Authorized Members",
  } = opts;

  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { currentAdviserEmail } = useAdminAdviserMails();

  const [adviser, setAdviser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resolvedEmail = useMemo(() => {
    // Priority:
    // 1) explicit override (Admin selection)
    // 2) current adviser email (for Administrators/External)
    // 3) current user email
    if (targetEmail && String(targetEmail).trim())
      return String(targetEmail).trim();

    if (
      (userRole === "Administrators" || userRole === "External") &&
      currentAdviserEmail &&
      String(currentAdviserEmail).trim()
    ) {
      return String(currentAdviserEmail).trim();
    }

    return (userEmail || "").trim();
  }, [targetEmail, userRole, currentAdviserEmail, userEmail]);

  const resolvedRole = useMemo(() => {
    // In your system, this is the role segment used in the Firestore path.
    // If you need admin to always lookup "Advisers", you can change it here.
    if (!roleOverride && (userRole === "Administrators" || userRole === "External")) {
      return "Advisers";
    }
    return roleOverride || userRole;
  }, [roleOverride, userRole]);

  const fetchAdviser = useCallback(async () => {
    setError("");
    if (!resolvedEmail || !resolvedRole) {
      setAdviser(null);
      return;
    }

    setLoading(true);
    try {
      const colRef = collection(
        firestore,
        `${collectionRoot}/${resolvedRole}/Data`,
      );

      const q = query(colRef, where("email", "==", resolvedEmail), limit(1));
      const snap = await getDocs(q);

      if (snap.empty) {
        setAdviser(null);
        return;
      }

      const docSnap = snap.docs[0];
      const normalized = normalizeAdviser(docSnap);

      if (userRole === "Administrators" || userRole === "External") {
        setAdviser({
          ...normalized,
          email: (userEmail || "").trim() || normalized.email,
        });
      } else {
        setAdviser(normalizeAdviser(docSnap));
      }
    } catch (e) {
      setAdviser(null);
      setError(e?.message || "Failed to fetch adviser details");
    } finally {
      setLoading(false);
    }
  }, [resolvedEmail, resolvedRole, collectionRoot, userRole, userEmail]);

  useEffect(() => {
    fetchAdviser();
  }, [fetchAdviser]);

  const entityType = useMemo(() => deriveEntityType(adviser), [adviser]);

  return {
    adviser, // normalized structure
    entityType, // Ltd_AR | Sole_Trader_AR | Trading_Style | unknown
    loading,
    error,
    refetch: fetchAdviser,

    // helpful debug/meta
    resolvedEmail,
    resolvedRole,
  };
}
